import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Select } from '@opendate-app/setlist';
import UpgradePaymentFields, {
  upgradeExpectedTotal, upgradePaymentMethod, upgradeReady, upgradeRefundAmount, useUpgradeQuote,
} from './UpgradePaymentFields';

const TicketTypeChangeModal = ({ ticket, ticketTypeOptions, paymentMethods, currency, submitting, onClose, onSubmit, onPreview, canRefund }) => {
  const [toTicketTypeId, setToTicketTypeId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [refundDifference, setRefundDifference] = useState(false);

  const current = ticket && ticketTypeOptions.find((option) => option.id === ticket.ticketTypeId);
  const quote = useUpgradeQuote(Boolean(toTicketTypeId), toTicketTypeId, onPreview);

  if (!ticket) return null;

  const options = ticketTypeOptions
    .filter((option) => current && option.id !== current.id && option.free === current.free)
    .map((option) => ({ value: option.id, label: option.name }));
  const ready = Boolean(toTicketTypeId) && upgradeReady(true, quote, paymentMethod);

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
      <Button onClick={() => onSubmit(toTicketTypeId, upgradePaymentMethod(quote, paymentMethod), upgradeExpectedTotal(quote, paymentMethod), upgradeRefundAmount(quote, refundDifference))} disabled={!ready || submitting}>
        {submitting ? 'Changing…' : 'Change ticket type'}
      </Button>
    </>
  );

  return (
    <Modal open onClose={onClose} icon={false} title="Change ticket type" footer={footer}>
      <p className="op-muted">Currently {ticket.productName}</p>
      <Select
        label="New ticket type"
        placeholder="Pick a ticket type"
        options={options}
        value={toTicketTypeId}
        onChange={(value) => { setToTicketTypeId(value); setPaymentMethod(null); setRefundDifference(false); }}
      />
      <UpgradePaymentFields
        changesTicketType={Boolean(toTicketTypeId)}
        quote={quote}
        paidAmount={ticket.finalCost}
        currency={currency}
        paymentMethods={paymentMethods}
        paymentMethod={paymentMethod}
        onChange={setPaymentMethod}
        canRefund={canRefund}
        refundDifference={refundDifference}
        onRefundDifferenceChange={setRefundDifference}
      />
    </Modal>
  );
};

TicketTypeChangeModal.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.number.isRequired,
    productName: PropTypes.string,
    ticketTypeId: PropTypes.number,
    finalCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  ticketTypeOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    free: PropTypes.bool,
  })),
  paymentMethods: PropTypes.arrayOf(PropTypes.string),
  currency: PropTypes.string,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  canRefund: PropTypes.bool,
};

TicketTypeChangeModal.defaultProps = {
  ticket: null,
  ticketTypeOptions: [],
  paymentMethods: [],
  currency: null,
  submitting: false,
  onPreview: null,
  canRefund: false,
};

export default TicketTypeChangeModal;
